import Card from '@mui/material/Card';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { getV1CompaniesCompanyIdForemenTypeahead } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { AutocompleteAsyncFormField } from '~components/FormFields/AutocompleteAsyncFormField';
import { PseudoLink } from '~components/Helpers/PseudoLink';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { contactTypesOptions, SystemRoles } from '~constants/enums';
import { ForemanTypeahead } from '~hooks/useForeman/models';
import { Job } from '~hooks/useJob/models';
import { Order } from '~hooks/useOrders/models';
import { ExtendedContactItem, Project } from '~hooks/useProjects/models';
import { User, UserBasic } from '~hooks/useUsers/models';
import useUsers from '~hooks/useUsers/useUsers';
import { EmptyContactItem } from '~pages/Sales/Projects/projectFormSchema';
import { ContactArrayItem } from '~pages/Sales/sharedSales/ContactArrayItem';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { ItemNameAndId } from '~types/ItemNameAndId';

interface ContactsProps {
  defaultData?: Project | Order | Job | null;
  isSalespersonRequired?: boolean;
  parentData?: Project | Order | null;
  userOwnsDefaultData?: boolean;
}

const getContactFormData = (contact: ExtendedContactItem) => {
  return {
    id: contact.id,
    name: contact.name,
    phone: contact.phone,
    type: contact.type as ItemNameAndId,
    isDeleted: false,
  };
};

export const Contacts = ({
  defaultData,
  isSalespersonRequired,
  parentData,
  userOwnsDefaultData = true,
}: ContactsProps) => {
  const {
    control,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();
  const { company, allContacts: watchedContactFields, project: watchedProject } = watch();

  const { getCompanyUsersTypeahead } = useUsers();

  const [contactTypesOptionsFiltered, setContactTypesOptionsFiltered] =
    useState(contactTypesOptions);

  // Handle the default values from the parentData if fields are not filled in
  const { salesContact, foremanContact, collaboratorContact, supervisorContact } =
    useMemo(
      () => ({
        salesContact: parentData?.isSalesContact ? parentData.salesContact : null,
        foremanContact: parentData?.isForemanContact ? parentData.foremanContact : null,
        collaboratorContact: parentData?.isCollaboratorContact
          ? parentData.collaboratorContact
          : null,
        supervisorContact: parentData?.isSupervisorContact
          ? parentData.supervisorContact
          : null,
      }),
      [parentData],
    );

  useEffect(() => {
    // Only load default values if the form is for create
    if (!defaultData?.id) {
      if (collaboratorContact && !defaultData?.isCollaboratorContact)
        prependContact(getContactFormData(collaboratorContact));

      if (foremanContact && !defaultData?.isForemanContact)
        prependContact(getContactFormData(foremanContact));

      if (salesContact && !defaultData?.isSalesContact)
        prependContact(getContactFormData(salesContact));

      if (supervisorContact && !defaultData?.isSupervisorContact)
        prependContact(getContactFormData(supervisorContact));
    }
  }, [
    collaboratorContact,
    foremanContact,
    salesContact,
    supervisorContact,
    defaultData?.id,
  ]);

  const {
    fields: contactFields,
    append: appendContact,
    prepend: prependContact,
    remove: removeContact,
    update: updateAllContacts,
  } = useFieldArray({
    control,
    name: 'allContacts',
  });

  useEffect(() => {
    if (watchedContactFields) {
      const res = contactTypesOptions.filter((singleType) => {
        // Check if any element in contactFields has the same type.id as singleType
        const found = watchedContactFields.some(
          (field: ExtendedContactItem) => field?.type?.id === singleType?.id,
        );
        return !found;
      });
      setContactTypesOptionsFiltered(res);
    }
  }, [watchedContactFields]);

  // A saved order already carries the people it was saved with, so re-merging
  // its own project's people on open would resurrect the ones the user
  // removed. Seeding the ref with the project the form opened on leaves the
  // cascade to run only once a different project is picked.
  const cascadedProjectIdRef = useRef<string | undefined>(
    getValues('project')?.id || undefined,
  );

  useEffect(() => {
    const selectedProjectId = watchedProject?.id || undefined;

    if (!selectedProjectId) {
      // Forgetting the cleared project lets re-picking it cascade again.
      cascadedProjectIdRef.current = undefined;

      return;
    }

    // The project entity lands asynchronously and outlives a deselection, so
    // only the one matching the current selection may cascade.
    const selectedProjectData = parentData?.id === selectedProjectId ? parentData : null;

    if (!selectedProjectData || cascadedProjectIdRef.current === selectedProjectId) {
      return;
    }

    cascadedProjectIdRef.current = selectedProjectId;

    const mergeProjectPeopleIntoField = (
      fieldName: string,
      projectPeople: Array<User | UserBasic>,
    ) => {
      const selectedPeople: Array<User | UserBasic> = getValues(fieldName);
      const peopleToAdd = projectPeople.filter((projectPerson) => {
        return !selectedPeople.some(
          (selectedPerson) => selectedPerson.id === projectPerson.id,
        );
      });

      if (peopleToAdd.length) {
        // Dirty on purpose: the order form builds a sparse PATCH from the
        // dirty map, so a clean write would show people it never submits.
        setValue(fieldName, [...selectedPeople, ...peopleToAdd], {
          shouldDirty: true,
        });
      }
    };

    mergeProjectPeopleIntoField('foremen', selectedProjectData.foremen);
    mergeProjectPeopleIntoField('collaborators', selectedProjectData.collaborators);
    mergeProjectPeopleIntoField('salespeople', selectedProjectData.salespeople);
  }, [watchedProject?.id, parentData, getValues, setValue]);

  const fetchForemen = useCallback(
    async (args: { limit?: number; query?: string; cursor?: string }) => {
      const companyId = company?.id ?? '';

      try {
        const response = await getV1CompaniesCompanyIdForemenTypeahead({
          path: { 'company-id': companyId },
          query: {
            'page[after]': args.cursor || undefined,
            'page[limit]': args.limit || MAX_API_PAGE_SIZE,
            'search[query]': args.query || undefined,
          },
        });

        const data = response.data.data.map((foreman) => ForemanTypeahead.parse(foreman));
        const pagination = extractPagination(response);

        return { data, pagination };
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.foremen.failed_to_fetch'));
      }
    },
    [company?.id],
  );

  const removeContactItem = (index: number) => {
    //New item, not sored in DB yet, just remove it
    if (!getValues('allContacts')?.[index].id) {
      removeContact(index);
    } else {
      // Previously saved item, mark it as deleted, in order to have their ids in the api call with destroy:1
      updateAllContacts(index, {
        ...contactFields[index],
        id: getValues('allContacts')?.[index]?.id as string,
        isDeleted: true,
      });
    }
  };

  return (
    <Card sx={{ mb: 2, p: 2, overflow: 'visible' }}>
      <Typography variant="form-section-title">{t('order.form.contacts')}</Typography>

      <Grid container sx={{ mt: 1 }}>
        <AutocompleteAsyncFormField
          asyncCallback={fetchForemen}
          control={control}
          disabled={!company?.id || !userOwnsDefaultData}
          errors={errors}
          filterSelectedOptions={false}
          getLabel={(item: ForemanTypeahead | null) => item?.fullName ?? ''}
          getValue={(item: ForemanTypeahead | null) => item?.id ?? ''}
          label={`${t('form_fields.assigned_foreman')}`}
          multiple
          name="foremen"
        />
      </Grid>

      <Grid container sx={{ mt: 1 }}>
        <AutocompleteAsyncFormField
          asyncCallback={getCompanyUsersTypeahead}
          control={control}
          disabled={!company?.id || !userOwnsDefaultData}
          errors={errors}
          extraRequestOptions={{ companyId: company?.id }}
          filterSelectedOptions={false}
          getLabel={(user: User | null) => user?.fullName ?? ''}
          getValue={(user: User | null) => user?.id ?? ''}
          label={`${t('form_fields.collaborator')}`}
          multiple
          name="collaborators"
        />
      </Grid>

      <Grid container sx={{ mt: 1 }}>
        <AutocompleteAsyncFormField
          asyncCallback={getCompanyUsersTypeahead}
          control={control}
          disabled={!company?.id || !userOwnsDefaultData}
          errors={errors}
          extraRequestOptions={{
            companyId: company?.id,
            roleName: SystemRoles.SALESPERSON,
          }}
          filterSelectedOptions={false}
          getLabel={(user: User) => `${user.firstName} ${user.lastName}`}
          getValue={(user: User) => user.id}
          isRequired={isSalespersonRequired}
          label={`${t('form_fields.contact_salesman')}`}
          multiple
          name="salespeople"
        />
      </Grid>

      <Grid container sx={{ mt: 1 }}>
        {contactFields.map((contact: { id: string; isDeleted?: boolean }, index) => {
          return contact.isDeleted ? null : (
            <ContactArrayItem
              key={contact.id}
              index={index}
              arrName={'allContacts'}
              errors={errors}
              control={control}
              removeAction={removeContactItem}
              typeOptions={contactTypesOptionsFiltered}
              disabled={!userOwnsDefaultData}
            />
          );
        })}

        {userOwnsDefaultData && (
          <Grid size={{ xs: 12 }}>
            <PseudoLink
              disabled={contactFields.length === contactTypesOptions.length}
              title={t('project.form.add_contact')}
              sx={{ mt: 1, display: 'inline-flex' }}
              action={
                contactFields.length !== contactTypesOptions.length
                  ? () => {
                      appendContact(EmptyContactItem);
                    }
                  : () => {}
              }
            />
          </Grid>
        )}
      </Grid>
    </Card>
  );
};
